/**
 * Spojenie s lokálnym bridge-om robota Reachy Mini (ws://127.0.0.1:8765).
 *
 * Jediná inštancia na celú stránku — importuje ju rozšírenie aj indikátor
 * v hornej lište, takže lišta vie o stave spojenia bez siahania do útrob VM.
 *
 * Protokol je popísaný v F:\REACHY_MINI\test\reachy_bridge\README.md.
 * Tri veci, ktoré tento súbor drží:
 *   1. WebSocket s automatickým obnovovaním (dieťa môže bridge zapnúť neskôr),
 *   2. mapu id → Promise, aby blok čakal presne na svoju odpoveď,
 *   3. zrkadlo senzorov, aby booleovské bloky odpovedali okamžite.
 */

const DEFAULT_URL = 'ws://127.0.0.1:8765';

/** Ako dlho čakať pred ďalším pokusom o spojenie. */
const RECONNECT_MS = 3000;

/**
 * Kód, ktorým nás bridge zavrie, keď robota prevzala iná otvorená stránka.
 * Po ňom sa zámerne NEpripájame späť — dve karty by si robota preberali
 * donekonečna a každé prevzatie ho vycentruje.
 */
const PREVZATE_KOD = 4001;

/**
 * Ako dlho po tlesknutí (a inom okamihovom jave) hlásiť „stalo sa".
 * Snímky chodia á 100 ms; toto okno musí byť dosť dlhé, aby klobúkový blok
 * stihol hranu zachytiť, a dosť krátke, aby sa dve tlesknutia nezlepili.
 */
const PULSE_MS = 250;

/** Snímok, keď o robotovi nič nevieme. Bloky tak vracajú neutrálne hodnoty. */
const NEUTRAL_SENSORS = {
    robot: false,
    tvar: {vidi: false, pocet: 0, x: 0, y: 0, size: 0},
    farby: {},
    jas: 0.5,
    hlasitost: 0,
    tlesknutia: 0,
    anteny: {left: false, right: false, pos_left: 0, pos_right: 0},
    hlava: {up: 0, turn: 0, tilt: 0, lift: 0},
    telo: 0
};

const cloneNeutral = () => JSON.parse(JSON.stringify(NEUTRAL_SENSORS));

class ReachyConnection {
    constructor () {
        this._sensors = cloneNeutral();

        this._url = DEFAULT_URL;
        this._ws = null;
        this._started = false;
        this._nextId = 1;
        this._pending = new Map();
        this._listeners = new Set();
        this._reconnectTimer = null;
        this._prevzate = false;      // robota drží iná karta
        this._sledujemNavrat = false;

        // Čo má bridge sledovať. Pri každom novom spojení sa to pošle znovu,
        // lebo bridge si po odpojení stránky sledovanie vypne.
        this._watchedColors = new Set();
        this._watchAntennas = false;

        this._clapCount = 0;
        this._clapPulseUntil = 0;
        this._prvySnimok = true;

        // Okamihové javy zo snímkov (tlesknutie, objavená tvár, stlačená
        // anténa). Rozšírenie na ne vešia klobúkové bloky.
        this._udalostiListeners = new Set();
        this._videlTvar = false;
        this._stlacenaAntena = false;
    }

    /**
     * Posledný snímok senzorov. Bez spojenia vracia neutrálne hodnoty, takže
     * bloky nikdy neodpovedajú podľa zastaraného stavu (`vidí tvár?` = nie).
     * @returns {object} - snímok podľa protokolu
     */
    get sensors () {
        return this.isConnected() ? this._sensors : NEUTRAL_SENSORS;
    }

    // -- životný cyklus ------------------------------------------------------

    /**
     * Spustí spojenie (a jeho obnovovanie). Volať sa dá opakovane.
     * @param {string} [url] - iná adresa bridge-u (používa to test)
     */
    start (url) {
        if (url) this._url = url;
        if (this._started) return;
        this._started = true;
        this._sledujNavrat();
        this._connect();
    }

    /**
     * Po prevzatí robota inou kartou sa hlásime späť, až keď je táto stránka
     * zase tá, na ktorú sa dieťa pozerá. Robota tak má vždy karta vpredu.
     */
    _sledujNavrat () {
        if (this._sledujemNavrat || typeof document === 'undefined') return;
        this._sledujemNavrat = true;
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) this._ziadajRobotaSpat();
        });
    }

    /** Vezmi si robota späť od inej karty (na požiadanie, nie automaticky). */
    _ziadajRobotaSpat () {
        if (!this._prevzate || !this._started) return;
        this._prevzate = false;
        this._connect();
    }

    /** Zavri spojenie a prestaň ho obnovovať (koniec testu, upratanie). */
    disconnect () {
        this._started = false;
        if (this._reconnectTimer) {
            clearTimeout(this._reconnectTimer);
            this._reconnectTimer = null;
        }
        if (this._ws) {
            try {
                this._ws.close();
            } catch (e) {
                // zatvorené spojenie sa zatvárať nedá - nevadí
            }
        }
    }

    _connect () {
        if (typeof WebSocket === 'undefined') return;
        if (this._ws && (this._ws.readyState === 0 || this._ws.readyState === 1)) return;

        let ws;
        try {
            ws = new WebSocket(this._url);
        } catch (e) {
            this._scheduleReconnect();
            return;
        }
        this._ws = ws;

        ws.onopen = () => {
            // Bridge o našich požiadavkách po odpojení nevie - zopakujeme ich.
            if (this._watchedColors.size) this._sendWatchedColors();
            if (this._watchAntennas) this._sendRaw({cmd: 'sleduj_anteny', zapni: true});
            this._notify();
        };

        ws.onmessage = event => this._onMessage(event.data);

        ws.onclose = event => {
            if (this._ws === ws) this._ws = null;
            // Robota nám prevzala iná karta. Keby sme sa hneď pripájali späť,
            // obe karty by si ho preberali donekonečna (á 3 s) a každé
            // prevzatie robota vycentruje — vyzeralo by to, že sa robot sám
            // vracia do stredu. Počkáme, kým oň naozaj požiada dieťa.
            this._prevzate = event && event.code === PREVZATE_KOD;
            this._onDisconnected();
            if (!this._prevzate) this._scheduleReconnect();
        };

        // Chybu netreba riešiť zvlášť - po nej vždy príde onclose.
        ws.onerror = () => {};
    }

    _scheduleReconnect () {
        if (!this._started || this._reconnectTimer) return;
        this._reconnectTimer = setTimeout(() => {
            this._reconnectTimer = null;
            this._connect();
        }, RECONNECT_MS);
    }

    _onDisconnected () {
        this._sensors = cloneNeutral();
        this._clapCount = 0;
        this._clapPulseUntil = 0;
        this._prvySnimok = true;
        this._videlTvar = false;
        this._stlacenaAntena = false;

        // Čakajúce bloky sa UVOĽNIA, nie zhodia - skript nesmie spadnúť ani
        // navždy visieť len preto, že dieťa vyplo bridge.
        const pending = this._pending;
        this._pending = new Map();
        pending.forEach(resolve => resolve({ok: false, chyba: 'Spojenie s robotom sa prerušilo.'}));

        this._notify();
    }

    // -- prijaté správy ------------------------------------------------------

    _onMessage (raw) {
        let data;
        try {
            data = JSON.parse(raw);
        } catch (e) {
            return;
        }

        if (data && data.typ === 'senzory') {
            this._updateSensors(data);
            return;
        }

        const resolve = this._pending.get(data.id);
        if (resolve) {
            this._pending.delete(data.id);
            resolve(data);
        }
    }

    _updateSensors (snapshot) {
        const bolPripojeny = this._sensors.robot;

        // Tlesknutie je monotónny čítač, nie boolean - okamihový jav by sa
        // pri kadencii 100 ms inak stratil. Hranu si preložíme na krátky pulz.
        //
        // Prvý snímok po pripojení iba zapíše, kde čítač práve je: bridge
        // počíta tlesknutia od svojho štartu, takže by sme inak všetky
        // doterajšie prečítali ako „práve teraz tlesklo" a klobúk „keď
        // tlesknem" by vystrelil hneď po pridaní do projektu.
        const clapCount = snapshot.tlesknutia || 0;
        const tlesklo = !this._prvySnimok && clapCount > this._clapCount;
        if (tlesklo) this._clapPulseUntil = Date.now() + PULSE_MS;
        this._clapCount = clapCount;

        // Hrany ostatných okamihových javov. Zrkadlo musí byť už nové, keď sa
        // klobúk spustí, preto sa udalosti hlásia až po priradení snímku.
        const vidiTvar = !!(snapshot.tvar || {}).vidi;
        const anteny = snapshot.anteny || {};
        const stlacena = !!(anteny.left || anteny.right);
        const objavilaSaTvar = !this._prvySnimok && vidiTvar && !this._videlTvar;
        const stlacilo = !this._prvySnimok && stlacena && !this._stlacenaAntena;
        this._videlTvar = vidiTvar;
        this._stlacenaAntena = stlacena;

        this._prvySnimok = false;
        this._sensors = snapshot;
        if (snapshot.robot !== bolPripojeny) this._notify();

        if (tlesklo) this._udalost('tlesknutie');
        if (objavilaSaTvar) this._udalost('tvar');
        if (stlacilo) this._udalost('antena');
    }

    // -- odosielanie ---------------------------------------------------------

    _sendRaw (obj) {
        if (!this.isConnected()) return false;
        try {
            this._ws.send(JSON.stringify(obj));
            return true;
        } catch (e) {
            return false;
        }
    }

    /**
     * Pošli príkaz a vráť Promise, ktorý sa splní, až keď robot pohyb dokončí.
     * Bez spojenia sa splní hneď - blok teda „nič nespraví" a program beží ďalej.
     * Zámerne bez časového stropu: `tancuj 15 sekúnd` legitímne odpovie až o 15 s.
     * @param {string} cmd - bodková cesta príkazu, napr. 'head.nod'
     * @param {object} [args] - pomenované argumenty príkazu
     * @returns {Promise} - odpoveď bridge-u ({ok, chyba, hodnota})
     */
    send (cmd, args) {
        if (!this.isConnected()) {
            // Blok je pokyn od dieťaťa - to je presne tá chvíľa, keď sa oplatí
            // pýtať si robota späť od inej karty.
            const prevzate = this._prevzate;
            this._ziadajRobotaSpat();
            return Promise.resolve({
                ok: false,
                chyba: prevzate
                    ? 'Robota ovláda iná otvorená stránka Scratchu. Skús to znovu.'
                    : 'Robot nie je pripojený.'
            });
        }

        const id = this._nextId++;
        return new Promise(resolve => {
            this._pending.set(id, resolve);
            if (!this._sendRaw({id, cmd, args: args || {}})) {
                this._pending.delete(id);
                resolve({ok: false, chyba: 'Robot nie je pripojený.'});
            }
        });
    }

    /** Červené tlačidlo: preruš pohyb, vyprázdni front, vráť robota domov. */
    stop () {
        this._sendRaw({cmd: 'stop'});
    }

    /**
     * Bridge sleduje len farby, o ktoré blok naozaj požiadal.
     * @param {string} color - anglický názov farby, napr. 'red'
     */
    watchColor (color) {
        if (!color || this._watchedColors.has(color)) return;
        this._watchedColors.add(color);
        this._sendWatchedColors();
    }

    _sendWatchedColors () {
        this._sendRaw({cmd: 'sleduj_farby', farby: Array.from(this._watchedColors)});
    }

    /**
     * Antény ako tlačidlá. Tlačidlový režim im vypína motory, preto sa zapína
     * až pri prvom použití bloku, ktorý stlačenie potrebuje (plán §11.7).
     */
    watchAntennas () {
        if (this._watchAntennas) return;
        this._watchAntennas = true;
        this._sendRaw({cmd: 'sleduj_anteny', zapni: true});
    }

    // -- stav ----------------------------------------------------------------

    /**
     * Beží bridge?
     * @returns {boolean} - true, ak je WebSocket otvorený
     */
    isConnected () {
        return !!this._ws && this._ws.readyState === 1;
    }

    /**
     * Je pripojený aj samotný robot? To hlási indikátor v lište.
     * @returns {boolean} - true, ak bridge beží a hlási pripojeného robota
     */
    isRobotReady () {
        return this.isConnected() && !!this.sensors.robot;
    }

    /**
     * Tlesklo sa práve teraz? (krátky pulz, na klobúkový blok)
     * @returns {boolean} - true krátko po každom novom tlesknutí
     */
    clapped () {
        return Date.now() < this._clapPulseUntil;
    }

    /**
     * Číslo posledného tlesknutia - bloky „čakaj na tlesknutie" merajú zmenu.
     * @returns {number} - monotónny čítač tlesknutí
     */
    clapCount () {
        return this._clapCount;
    }

    // -- odber zmien (pre indikátor v lište) ---------------------------------

    /**
     * Prihlás sa na zmeny stavu spojenia.
     * @param {Function} listener - dostane true/false podľa isRobotReady()
     * @returns {Function} - zavolaj na odhlásenie
     */
    subscribe (listener) {
        this._listeners.add(listener);
        return () => this._listeners.delete(listener);
    }

    /**
     * Prihlás sa na okamihové javy: 'tlesknutie', 'tvar', 'antena'.
     *
     * Klobúkové bloky sa spúšťajú odtiaľto, nie skúšaním každý snímok. VM
     * totiž pri každom skúšaní založí a zruší vlákno, a aj zrušené vlákno
     * ráta ako „program beží" — zelená vlajka by potom svietila stále
     * (runtime.js: „Add done threads so that even if a thread finishes
     * within 1 frame, the green flag will still indicate that a script ran").
     * @param {Function} listener - dostane názov javu
     * @returns {Function} - zavolaj na odhlásenie
     */
    onUdalost (listener) {
        this._udalostiListeners.add(listener);
        return () => this._udalostiListeners.delete(listener);
    }

    _udalost (nazov) {
        this._udalostiListeners.forEach(listener => {
            try {
                listener(nazov);
            } catch (e) {
                // Jeden pokazený klobúk nesmie zastaviť ostatné.
            }
        });
    }

    _notify () {
        this._listeners.forEach(listener => {
            try {
                listener(this.isRobotReady());
            } catch (e) {
                // Indikátor nesmie zhodiť spojenie.
            }
        });
    }
}

const connection = new ReachyConnection();

export default connection;
export {NEUTRAL_SENSORS};
